import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, XIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { useLanguage } from '../contexts/LanguageContext';
import { photos, filterKeys, type FilterKey, type Photo } from '../data/media';
import { mediaText } from '../data/mediaText';
import { ui } from '../data/ui';
export function Portfolio() {
  const {
    language
  } = useLanguage();
  const strings = ui[language];
  const text = mediaText[language];
  const [filter, setFilter] = useState<FilterKey>('all');
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const visiblePhotos = useMemo(() => filter === 'all' ? photos : photos.filter(photo => photo.category === filter), [filter]);
  return <div className="w-full bg-blush-100" data-id="element-125" data-mp="199at8e04h9yo">
      <section className="mx-auto max-w-6xl px-5 pb-10 pt-12 sm:px-8 sm:pt-16" data-id="element-126" data-mp="19ac9qxzwqons">
        <p className="text-xs uppercase tracking-[0.3em] text-ink/50" data-id="element-127" data-mp="158h6vp2bw37w">
          {strings.portfolio.eyebrow}
        </p>
        <h1 className="mt-3 font-script text-5xl text-ink sm:text-6xl" data-id="element-128" data-mp="16895rgkvg5xc">
          {strings.portfolio.title}
        </h1>
        <div className="mt-5 h-1.5 w-full bg-blush-300" data-id="element-129" data-mp="1g8jjr3eqdhdk" />
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ink/75 sm:text-base" data-id="element-130" data-mp="1g6ysbmnpqejx">
          {strings.portfolio.intro}
        </p>
      </section>

      {/* Photos */}
      <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8" data-id="element-131" data-mp="1px1k5jj3lmgo">
        <SectionHeading data-id="element-132" data-mp="1tzeny2eabaat">{strings.sections.photos}</SectionHeading>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label={strings.portfolio.filterLabel} data-id="element-133" data-mp="1u1re32u74qyy">
          {filterKeys.map(key => {
          const active = key === filter;
          return <button key={key} type="button" role="tab" aria-selected={active} onClick={() => setFilter(key)} className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${active ? 'bg-ink text-blush-50' : 'border border-ink/20 text-ink/60 hover:bg-white hover:text-ink'}`} data-id="element-134" data-mp="4ljhizwtwdtv">
                {text.categories[key]}
              </button>;
        })}
        </div>

        <motion.ul layout className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-135" data-mp="kgy4nnunte67">
          <AnimatePresence mode="popLayout" data-id="element-136" data-mp="gh7gzt0fje5w">
            {visiblePhotos.map(photo => {
            const meta = text.photos[photo.id];
            return <motion.li key={photo.id} layout initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-137" data-mp="u9nfqhmasq10">
                  <button type="button" onClick={() => setLightbox(photo)} className="group block w-full overflow-hidden bg-white text-left" data-id="element-138" data-mp="1isyw6x5r9a45">
                    <span className="block overflow-hidden" data-id="element-139" data-mp="18vb12648m0lg">
                      <img src={photo.src} alt={meta.alt} loading="lazy" className="aspect-[2/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-140" data-mp="1dtm8ybaz2111" />
                    </span>
                    <span className="flex items-baseline justify-between gap-3 px-4 py-3" data-id="element-141" data-mp="q4jqx2cfe660">
                      <span className="text-sm font-medium text-ink" data-id="element-142" data-mp="v2v0dbyt6enp">{meta.caption}</span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45" data-id="element-143" data-mp="g640z2p4x2y3">
                        {text.categories[photo.category]}
                      </span>
                    </span>
                  </button>
                </motion.li>;
          })}
          </AnimatePresence>
        </motion.ul>
      </section>

      {/* Reels */}
      <section className="bg-blush-200/60 py-16" data-id="element-144" data-mp="1jhjf4hc48haq">
        <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-145" data-mp="1ep9kwp3ywv2n">
          <SectionHeading variant="plain" data-id="element-146" data-mp="1enopkx9tx83q">{strings.sections.reels}</SectionHeading>
          <p className="mt-4 max-w-2xl text-sm text-ink/70" data-id="element-147" data-mp="19qokopopube7">{strings.portfolio.reelsIntro}</p>
          <div className="mt-8 flex min-h-[340px] flex-col items-center justify-center gap-6 bg-blush-100 px-6 py-14" data-id="element-148" data-mp="14teu36plbtos">
            <img src="https://cdn.magicpatterns.com/uploads/3F4XeLdDYSjonep86TPRpo/image.png" alt="" aria-hidden="true" className="w-full max-w-md" data-id="element-149" data-mp="10smnkl83s2w7" />
            <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-ink/70" data-id="element-150" data-mp="2q56o9icjzhje">
              <PlayIcon className="h-5 w-5" aria-hidden="true" data-id="element-151" data-mp="2l5lnr3hqj14k" />
              Coming soon
            </p>
          </div>
        </div>
      </section>

      {/* Snippets */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8" style={{
      backgroundColor: "#EED9D7"
    }} data-id="element-152" data-mp="2sns34fxmgh9h">
        <SectionHeading data-id="element-153" data-mp="32ffpw3j8ha4m">{strings.sections.snippets}</SectionHeading>
        <div className="mt-8 flex min-h-[340px] flex-col items-center justify-center gap-6 bg-blush-200/60 px-6 py-14" style={{
        backgroundColor: "#F6E7E6"
      }} data-id="element-154" data-mp="32g7n1ishbfx0">
          <img src="https://cdn.magicpatterns.com/uploads/3F4XeLdDYSjonep86TPRpo/image.png" alt="" aria-hidden="true" className="w-full max-w-md" style={{
          height: "211px"
        }} data-id="element-155" data-mp="29rtp131e4jlg" />
          <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-ink/70" data-id="element-156" data-mp="2joeufphbnwpt">
            <PlayIcon className="h-5 w-5" aria-hidden="true" data-id="element-157" data-mp="2op2jw6pwk5hx" />
            Coming soon
          </p>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence data-id="element-158" data-mp="3v6fw8l51epzg">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4" role="dialog" aria-modal="true" aria-label={text.photos[lightbox.id].caption} onClick={() => setLightbox(null)} data-id="element-159" data-mp="3wr1041iombr">
            <button type="button" onClick={() => setLightbox(null)} aria-label={strings.portfolio.closeLabel} className="absolute right-5 top-5 rounded-full bg-blush-50/90 p-2 text-ink" data-id="element-160" data-mp="24zrrd4igw3uh">
              <XIcon className="h-5 w-5" data-id="element-161" data-mp="1c9j9ldruflgf" />
            </button>
            <motion.figure initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="max-h-full w-full max-w-3xl" onClick={event => event.stopPropagation()} data-id="element-162" data-mp="1q4bvqv28nzvz">
              <img src={lightbox.src} alt={text.photos[lightbox.id].alt} className="max-h-[75vh] w-full object-contain" data-id="element-163" data-mp="jgeg8ji7n1av" />
              <figcaption className="mt-3 text-center text-sm uppercase tracking-[0.2em] text-blush-50" data-id="element-164" data-mp="9j0abdtwxuwt">
                {text.photos[lightbox.id].caption}
              </figcaption>
            </motion.figure>
          </motion.div>}
      </AnimatePresence>
    </div>;
}